import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../../system/layout/SettingsListPage";
import DigitalSigningTemplateCreateDialog from "./DigitalSigningTemplateCreateDialog";
import {
    CREATE_TEMPLATE_PARAM,
    DIGITAL_SIGNING_TEMPLATES_PATH,
    DigitalSigningTemplateListRow,
    DigitalSigningTemplateListSortField
} from "./digitalSigningTemplate.model";
import {DigitalSigningTemplateService} from "./digitalSigningTemplate.service";

export const DIGITAL_SIGNING_TEMPLATES_TABLE_ID = 'system-digital-signing-templates';

const sortedBy = (field: DigitalSigningTemplateListSortField) => ({isServerSortable: true, serverSortField: field});

const DigitalSigningTemplatesPage = () => {
    useTrackScreenVisit('system digital signing');

    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [createOpen, setCreateOpen] = useState(() => searchParams.get(CREATE_TEMPLATE_PARAM) === '1');

    useEffect(() => {
        if (!searchParams.has(CREATE_TEMPLATE_PARAM)) return;
        const next = new URLSearchParams(searchParams);
        next.delete(CREATE_TEMPLATE_PARAM);
        setSearchParams(next, {replace: true});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = useMemo<ColumnDef<DigitalSigningTemplateListRow>[]>(() => {
        const names = (references: {name?: string}[]) => references.map(reference => reference.name).filter(Boolean).join(', ');
        const sectorsOf = (row: DigitalSigningTemplateListRow) => (row.sectors.length ? names(row.sectors) : t('Not offered'));

        return [
            {
                id: 'name',
                header: t('Name'),
                accessorFn: row => row.name,
                cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
                size: 260,
                meta: {defaultActive: true, grow: true, ...sortedBy('name')},
            },
            {
                id: 'sectors',
                header: t('Sectors'),
                accessorFn: sectorsOf,
                cell: ({getValue}) => <Box>{getValue<string>()}</Box>,
                size: 220,
                meta: {defaultActive: true},
            },
            {
                id: 'id',
                header: t('ID'),
                accessorFn: row => row.id,
                cell: ({row}) => <Box>{row.original.id}</Box>,
                size: 260,
                meta: {defaultActive: false, ...sortedBy('id')},
            },
            {
                id: 'updateDate',
                header: t('Last updated'),
                accessorFn: row => row.updateDate,
                cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
                size: 200,
                meta: {defaultActive: true, ...sortedBy('updateDate')},
            },
        ];
    }, [t]);

    const controller = useServerTable<DigitalSigningTemplateListRow>({
        tableId: DIGITAL_SIGNING_TEMPLATES_TABLE_ID,
        columns,
        getRowId: row => row.id,
        initialSorting: [{id: 'name', desc: false}],
        onRowClick: row => navigate(`${DIGITAL_SIGNING_TEMPLATES_PATH}/${row.id}`),
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await DigitalSigningTemplateService.listRows({
                search: searchQuery || undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        },
    });

    return (
        <SettingsListPage title={t('Digital signing')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate label={t('Create template')} onClick={() => setCreateOpen(true)}/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            {createOpen && <DigitalSigningTemplateCreateDialog open onClose={() => setCreateOpen(false)}/>}
        </SettingsListPage>
    );
};

export default DigitalSigningTemplatesPage;
